export const SECTIONS = [
  {
    id: "identity",
    title: "Business Identity & Concept",
    blurb: "Who you are, what you stand for, and who you serve.",
    fields: [
      { key: "business_name", label: "Business Name", placeholder: "e.g. Golden Hour Bakery" },
      { key: "tagline", label: "Tagline", placeholder: "A short line that sells the feeling" },
      { key: "industry", label: "Industry / Niche", placeholder: "e.g. Beauty, Fitness, E-commerce, Food" },
      { key: "mission", label: "Mission Statement", long: true, placeholder: "Why does this business exist?" },
      { key: "target_audience", label: "Target Audience Profile", long: true, placeholder: "Age, lifestyle, pains, desires — who exactly buys this?" },
    ],
  },
  {
    id: "legal",
    title: "Legal & Structural Foundation",
    blurb: "Make it official — entity, registration, and paperwork.",
    links: [
      { label: "Apply for an EIN (IRS)", href: "https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online" },
      { label: "SBA — Register Your Business", href: "https://www.sba.gov/business-guide/launch-your-business/register-your-business" },
      { label: "SBA — Licenses & Permits", href: "https://www.sba.gov/business-guide/launch-your-business/apply-licenses-permits" },
      { label: "USPTO — Register a Trademark", href: "https://www.uspto.gov/trademarks/apply" },
      { label: "USPTO — Trademark Search", href: "https://www.uspto.gov/trademarks/search" },
      { label: "U.S. Copyright Office — Register a Copyright", href: "https://www.copyright.gov/registration/" },
    ],
    fields: [
      { key: "entity_type", label: "LLC / Entity Type", placeholder: "LLC, Sole Proprietor, S-Corp…" },
      { key: "state", label: "State of Registration", placeholder: "e.g. Texas" },
      { key: "ein_status", label: "EIN / Tax ID Status", placeholder: "Not started / Applied / Received + notes" },
      { key: "operating_agreement", label: "Operating Agreement Notes", long: true, placeholder: "Ownership split, roles, key terms" },
      { key: "licenses", label: "Licenses & Permits Needed", long: true, placeholder: "Local, state, industry-specific" },
      { key: "ip_status", label: "Trademark & Copyright Status", long: true, placeholder: "Name / logo trademark search done? Filed? Copyrights registered for content, designs, photos?" },
    ],
  },
  {
    id: "domain",
    title: "Domain & Web Footprint",
    blurb: "Claim your corner of the internet.",
    links: [
      { label: "GoDaddy", href: "https://www.godaddy.com" },
      { label: "Namecheap", href: "https://www.namecheap.com" },
      { label: "Squarespace Domains", href: "https://domains.squarespace.com" },
      { label: "Cloudflare", href: "https://www.cloudflare.com" },
    ],
    fields: [
      { key: "domain_name", label: "Primary Domain Name", placeholder: "e.g. goldenhourbakery.com" },
      { key: "registrar", label: "Domain Registrar", placeholder: "GoDaddy, Namecheap… where you bought / will buy it" },
      { key: "dns_notes", label: "DNS & Hosting Notes", long: true, placeholder: "Where it points, hosting provider, renewal date" },
      { key: "business_email", label: "Business Email Addresses", placeholder: "hello@yourdomain.com — Google Workspace, Proton, Zoho…" },
    ],
  },
  {
    id: "branding",
    title: "Brand Palette & Visual System",
    blurb: "Logo, colors, fonts, and the voice that ties it together.",
    links: [
      { label: "Coolors — Palette Generator", href: "https://coolors.co" },
      { label: "Google Fonts", href: "https://fonts.google.com" },
      { label: "Fontshare", href: "https://www.fontshare.com" },
      { label: "Looka — Logo Ideas", href: "https://looka.com" },
    ],
    fields: [
      { key: "logo_notes", label: "Logo Ideas / Asset Links", long: true, placeholder: "Concepts, designer links, files" },
      { key: "primary_colors", label: "Primary Brand Colors (Hex)", swatch: true, placeholder: "#A8785A, #FAF6EF" },
      { key: "accent_colors", label: "Secondary & Accent Colors", swatch: true, placeholder: "#C39B7B, #D4A0B4" },
      { key: "heading_font", label: "Primary & Heading Fonts", placeholder: "e.g. Cabinet Grotesk" },
      { key: "body_font", label: "Body & Mono Fonts", placeholder: "e.g. Plus Jakarta Sans" },
      { key: "brand_voice", label: "Brand Voice / Tone", long: true, placeholder: "Bold? Soft? Playful? Luxury? How do you sound?" },
    ],
  },
  {
    id: "socials",
    title: "Social Media Ecosystem",
    blurb: "Every handle, every profile, one place.",
    links: [
      { label: "Instagram — Sign Up", href: "https://www.instagram.com/accounts/emailsignup/" },
      { label: "Facebook — Create a Page", href: "https://www.facebook.com/pages/create" },
      { label: "TikTok for Business", href: "https://www.tiktok.com/business/en" },
      { label: "LinkedIn — Create a Page", href: "https://www.linkedin.com/company/setup/new/" },
      { label: "Linktree", href: "https://linktr.ee" },
    ],
    fields: [
      { key: "instagram", label: "Instagram Handle & Link", placeholder: "@yourhandle + profile URL" },
      { key: "facebook", label: "Facebook Page & Link", placeholder: "Page name + URL" },
      { key: "tiktok", label: "TikTok Profile & Link", placeholder: "@yourhandle + URL" },
      { key: "linkedin_x", label: "LinkedIn / X Links", placeholder: "Company page + X profile" },
      { key: "bio_link", label: "Bio Template & Link-in-Bio Setup", long: true, placeholder: "Your bio copy + Linktree / Beacons link" },
    ],
  },
  {
    id: "offerings",
    title: "Products & Services Suite",
    blurb: "Exactly what you sell and what it costs.",
    fields: [
      { key: "core_offer", label: "Core Offer Description", long: true, placeholder: "The main thing people buy from you" },
      { key: "pricing", label: "Pricing Structure", long: true, placeholder: "Tiers, packages, hourly, retainers…" },
      { key: "deliverables", label: "Deliverables / Features List", long: true, placeholder: "What the customer actually receives" },
      { key: "upsells", label: "Upsells & Add-ons", long: true, placeholder: "Extras that raise order value" },
    ],
  },
  {
    id: "finances",
    title: "Financials & Operations",
    blurb: "Money in, money out, and the target you're chasing.",
    links: [
      { label: "Stripe", href: "https://stripe.com" },
      { label: "PayPal Business", href: "https://www.paypal.com/us/business" },
      { label: "Mercury — Business Banking", href: "https://mercury.com" },
      { label: "Wave — Free Accounting", href: "https://www.waveapps.com" },
    ],
    fields: [
      { key: "payment_processors", label: "Payment Processors", placeholder: "Stripe, PayPal, Square, Cash App…" },
      { key: "bank_status", label: "Business Bank Account Status", placeholder: "Not opened / Researching / Opened at…" },
      { key: "monthly_overhead", label: "Monthly Overhead Budget", long: true, placeholder: "Tools, rent, ads, software — list them with $" },
      { key: "revenue_target", label: "Target Monthly Revenue", placeholder: "e.g. $5,000/mo by month 6" },
    ],
  },
  {
    id: "goals",
    title: "Strategic Roadmap & Goals",
    blurb: "The next 30 and 90 days, measured honestly.",
    fields: [
      { key: "milestones_30", label: "30-Day Launch Milestones", long: true, placeholder: "What must be true 30 days from now?" },
      { key: "goals_90", label: "90-Day Scale Goals", long: true, placeholder: "Revenue, audience, offers, hires…" },
      { key: "kpis", label: "Key Performance Indicators (KPIs)", long: true, placeholder: "Followers, conversion rate, AOV, email subs…" },
      { key: "bottlenecks", label: "Current Bottlenecks", long: true, placeholder: "What's slowing you down right now?" },
    ],
  },
  {
    id: "notes",
    title: "Notes & Attachments",
    blurb: "Your free-form space — meeting notes, research, mood boards, tables, charts, images and video, all in one living page.",
    fields: [
      { key: "body", label: "Notes", doc: true, placeholder: "Start typing… Use the toolbar to format text, add bullet points, tables, charts, images or video." },
    ],
  },
];

export const emptyData = () =>
  Object.fromEntries(SECTIONS.map((s) => [s.id, Object.fromEntries(s.fields.map((f) => [f.key, ""]))]));

export const mergeData = (server = {}) =>
  Object.fromEntries(SECTIONS.map((s) => [s.id, Object.fromEntries(s.fields.map((f) => [f.key, server?.[s.id]?.[f.key] ?? ""]))]));
